import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { ModalService } from 'projects/shared/modal/modal.service';
import { MortgageApplication } from '../../../models/mortgage-app.model';
import { Asset } from '../../../models/asset.model';
import { AssetDialogComponent, BulkSaveTransaction } from '../asset-dialog/asset-dialog.component';
import { AssetItem } from '../../../models/asset-item-model';
import { Borrower } from '../../../models/borrower-model';
import { EnumsService } from '../../../services/enums.service';
import { WizardFlowServiceBase } from '../../../services/wizard/wizard-flow-service.base';
import * as _ from 'lodash';
import { AssetType, assetTypeRequiresInstitution } from '../../../models/enums/asset-type-enum';

export interface AssetListGroupEntry {
  kind: 'group';
  depository: string;
  groupTotal: number;
  children: AssetItem[];
}

export interface AssetListStandaloneEntry {
  kind: 'standalone';
  item: AssetItem;
}

export type AssetListEntry = AssetListGroupEntry | AssetListStandaloneEntry;

@Component({
    selector: 'asset-list',
    templateUrl: 'asset-list.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssetListComponent {

  private modalOptions: NgbModalOptions = {
    size: "lg",
    backdrop: "static",
    container: "body"
  };

  private modalOptionsBulk: NgbModalOptions = {
    size: "xl",
    backdrop: "static",
    container: "body"
  };

  private _mortgageApplication: MortgageApplication;

  private _subscriptionToAssetSavedForCreate: any;
  private _subscriptionToAssetSavedForEdit: any;

  private _assets: Asset[] = [];

  @Input()
  public set assets(assets: Asset[]) {
    this._assets = assets;
    this.populateAssetItems(this._assets);
    this._cdr.markForCheck();
  }

  @Input()
  fieldConfig: any;

  private _enableGrouping: boolean = false;

  @Input()
  public set enableGrouping(value: boolean) {
    this._enableGrouping = !!value;
    this.populateAssetItems(this._assets);
    this._cdr.markForCheck();
  }
  public get enableGrouping(): boolean {
    return this._enableGrouping;
  }

  @Output()
  assetEdited: EventEmitter<Asset> = new EventEmitter<Asset>();

  @Output()
  assetDeleted: EventEmitter<number> = new EventEmitter<number>();

  @Output()
  addAtInstitutionClicked: EventEmitter<string> = new EventEmitter<string>();

  assetItems: AssetItem[] = [];

  listEntries: AssetListEntry[] = [];

  constructor(
    private readonly _modalService: ModalService,
    private readonly _wizardFlowService: WizardFlowServiceBase,
    private readonly _enumsService: EnumsService,
    private readonly _cdr: ChangeDetectorRef
  ) {
    this._mortgageApplication = this._wizardFlowService.context.mortgage;
  }

  public get totalAssets(): number {
    let sum: number = 0;
    this.assetItems.forEach(asset => {
      sum = sum + Number(asset.value);
    });
    return sum;
  }

  /**
   * Re-renders the list from the current `_assets` reference. Use this when
   * the parent has mutated `mortgage.assets` in place (push, splice, entity
   * state change) and needs the list to refresh — instead of re-assigning a
   * new filtered array, which would diverge `_assets` from `mortgage.assets`
   * and break later in-place mutations (e.g. trash splice for negative-id
   * assets would no longer propagate back to the parent's array).
   */
  public refresh(): void {
    this.populateAssetItems(this._assets);
    this._cdr.markForCheck();
  }

  public isValid(): boolean {
    let result: boolean = true;
    this.assetItems.forEach((assetItem) => {
      if (!assetItem.isValid) {
        result = false;
      }
    });
    return result;
  }

  private populateAssetItems(assets: Asset[]) {
    this.assetItems = [];
    this.listEntries = [];
    const groupByKey = new Map<string, AssetListGroupEntry>();

    assets?.forEach(asset => {
      if (asset.entityState === 'Deleted') {
        return;
      }
      const assetItem = this.toAssetItem(asset);
      if (!assetItem) {
        return;
      }
      this.assetItems.push(assetItem);

      const depository = (asset.depository ?? '').trim();

      if (!this._enableGrouping || !depository || !assetTypeRequiresInstitution(asset.assetType)) {
        this.listEntries.push({ kind: 'standalone', item: assetItem });
        return;
      }

      const groupKey = depository.toLowerCase();
      let group = groupByKey.get(groupKey);
      if (!group) {
        group = {
          kind: 'group',
          depository,
          children: [],
          groupTotal: 0,
        };
        groupByKey.set(groupKey, group);
        this.listEntries.push(group);
      }
      group.children.push(assetItem);
    });

    // Demote single-child groups to standalone entries; compute group totals for the rest.
    this.listEntries = this.listEntries.flatMap<AssetListEntry>(entry => {
      if (entry.kind === 'group') {
        if (entry.children.length < 2) {
          return entry.children.map(child => ({ kind: 'standalone', item: child }));
        }
        entry.groupTotal = entry.children.reduce((sum, c) => sum + Number(c.value ?? 0), 0);
      }
      return [entry];
    });
  }

  private isAssetValid(asset: Asset) {
    if (!this._enumsService.assetTypes.find(a => a.value == asset.assetType)) {
      return false;
    }
    if (!asset.owningBorrowerIds || asset.owningBorrowerIds.length === 0) {
      return false;
    }
    if (asset.cashMarketValue == null || Number(asset.cashMarketValue) <= 0) {
      return false;
    }
    if (asset.assetType === AssetType.Automobile) {
      if (!asset.makeModel || asset.makeModel.length === 0 || asset.year == null) {
        return false;
      }
    }
    if (asset.assetType === AssetType.CheckingAccount) {
      if (this.fieldConfig && !this.fieldConfig['asset.depository'].isHidden && this.fieldConfig['asset.depository'].isRequired) {
        if (!asset.depository || asset.depository.length === 0) {
          return false;
        }
      }
      if (this.fieldConfig && !this.fieldConfig['asset.accountNumber'].isHidden && this.fieldConfig['asset.accountNumber'].isRequired) {
        if (!asset.accountNumber || asset.accountNumber.length === 0) {
          return false;
        }
      }
    }
    if (asset.assetType === AssetType.PendingNetSaleProceedsFromRealEstateAssets) {
      if (!asset.holderAddress1 || asset.holderAddress1.length === 0 ||
        !asset.holderCity || asset.holderCity.length === 0 ||
        !asset.holderState || asset.holderState.length === 0 ||
        !asset.holderZipCode || asset.holderZipCode.length === 0) {
        return false;
      }
    }
    return true;
  }

  private toAssetItem = (asset: Asset): AssetItem | undefined => {
    const owningBorrowerIds: number[] = asset.owningBorrowerIds;
    if (this._wizardFlowService.inCoApplyFlow) {
      // In co-apply flow, you're not supposed to see assets that you do not have ownership on
      if (!asset.owningBorrowerIds.includes(this._wizardFlowService.context.currentBorrower.borrowerId!)) {
        return undefined;
      }
    }
    let assetOwners: string = "";
    const depository = (asset.depository ?? '').trim() || undefined;
    if (!owningBorrowerIds) {
      return new AssetItem(asset.assetId!, asset.assetType!, asset.cashMarketValue!, assetOwners, this.isAssetValid(asset), depository);
    }

    for (let i = 0; i < owningBorrowerIds.length; i++) {
      const borrower: Borrower | undefined = this._mortgageApplication.borrowers.find(b => b.borrowerId === owningBorrowerIds[i]);
      if (borrower) {
        assetOwners = assetOwners + " " + borrower.firstName + " " + borrower.lastName;
      }
      if (i != owningBorrowerIds.length - 1) {
        assetOwners = assetOwners + ",";
      }
    }
    const assetItem = new AssetItem(asset.assetId!, asset.assetType!, asset.cashMarketValue!, assetOwners, this.isAssetValid(asset), depository);
    if (this._wizardFlowService.inCoApplyFlow) {
      // If we're in co-apply flow and there is more than one owner on the asset (other than the current borrower), the asset will be readonly
      assetItem.isReadOnly = asset.owningBorrowerIds.length > 1;
    }
    return assetItem;
  }

  private showAssetPopup(asset?: Asset) {
    const isBulk = !!asset && assetTypeRequiresInstitution(asset.assetType) && !!asset.depository;
    const modal = this._modalService.show(AssetDialogComponent, isBulk ? this.modalOptionsBulk : this.modalOptions);
    modal.componentInstance.fieldsToConfig = this.fieldConfig;
    modal.componentInstance.existingInstitutions = this.getExistingInstitutions(asset);
    if (asset) {
      modal.componentInstance.asset = asset;
      // Editing an institution-based asset: lock its institution so the dialog
      // opens in bulk-edit mode — the user can correct this row AND see/edit
      // every sibling at the same bank without a second trip through the dialog.
      if (assetTypeRequiresInstitution(asset.assetType) && asset.depository) {
        modal.componentInstance.lockedInstitution = asset.depository;
        modal.componentInstance.seedAssets = this.getAssetsAtInstitution(asset.depository);
      }
    }
    this._subscriptionToAssetSavedForCreate = modal.componentInstance.saveClickedEmitterForCreate
      .subscribe((e: Asset) => this.onSaveClickedOnAssetModalForCreate(e));
    this._subscriptionToAssetSavedForEdit = modal.componentInstance.saveClickedEmitterForEdit
      .subscribe((e: Asset) => this.onSaveClickedOnAssetModalForEdit(e));
    modal.componentInstance.bulkSavedEmitter
      .subscribe((tx: BulkSaveTransaction) => this.applyBulkSaveTransaction(tx));
  }

  /**
   * Single-pass reconciliation of the dialog's local row list against the
   * assets array. Deletes first (freeing up assetIds), then upserts. Only
   * fires on Ok — Cancel never emits, so in-dialog edits/adds/deletes are
   * disposable until the user confirms.
   */
  private applyBulkSaveTransaction = (tx: BulkSaveTransaction): void => {
    const assets = this._assets;
    if (!assets) return;

    for (const assetId of tx.removeAssetIds) {
      const idx = assets.findIndex(a => a.assetId === assetId);
      if (idx === -1) continue;
      const target = assets[idx];
      if (target.assetId! < 0) {
        assets.splice(idx, 1);
      } else {
        target.entityState = 'Deleted';
        target.requireEntityStateForChanges = true;
      }
    }

    for (const asset of tx.upsert) {
      const existing = assets.find(a => a.assetId === asset.assetId && a.entityState !== 'Deleted');
      if (existing) {
        asset.requireEntityStateForChanges = true;
        asset.entityState = 'Modified';
        assets[assets.indexOf(existing)] = asset;
        this.assetEdited.emit(asset);
      } else {
        asset.requireEntityStateForChanges = true;
        asset.entityState = 'Added';
        assets.push(asset);
      }
    }

    this.populateAssetItems(assets);
    this._cdr.markForCheck();
  }

  /**
   * Returns every non-deleted asset whose depository matches the given
   * institution name (case-insensitive). Used to seed the bulk dialog's row
   * table so the user sees the full set of accounts at the bank.
   */
  private getAssetsAtInstitution(depository: string): Asset[] {
    const needle = (depository ?? '').trim().toLowerCase();
    if (!needle) return [];
    return (this._assets ?? []).filter(a =>
      a.entityState !== 'Deleted' &&
      (a.depository ?? '').trim().toLowerCase() === needle
    );
  }

  private getExistingInstitutions(currentAsset?: Asset): string[] {
    const set = new Set<string>();
    (this._assets ?? []).forEach(a => {
      if (a === currentAsset) return;
      if (a.entityState === 'Deleted') return;
      const value = (a.depository ?? '').trim();
      if (value) set.add(value);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  private onSaveClickedOnAssetModalForCreate = (asset: Asset): void => {
    asset.requireEntityStateForChanges = true;
    asset.entityState = 'Added';
    this._assets?.push(asset);
    this.populateAssetItems(this._assets);
    this._cdr.markForCheck();
  }

  private onSaveClickedOnAssetModalForEdit = (asset: Asset): void => {
    const assets = this._assets;
    asset.requireEntityStateForChanges = true;
    asset.entityState = 'Modified';
    if (assets) {
      const existingAsset = assets?.find(e => e.assetId === asset.assetId);
      if (existingAsset) {
        const index = assets?.indexOf(existingAsset);
        assets[index] = asset;
        this.assetEdited.emit(asset);
      }
    }
    this.populateAssetItems(this._assets);
    this._cdr.markForCheck();
  }

  public onAssetDeleted = (asset: AssetItem) => {
    const assets = this._assets;
    if (!assets) {
      this.populateAssetItems(this._assets);
      this._cdr.markForCheck();
      return;
    }
    const assetToDelete = assets.find(i => i.assetId === asset.id);
    if (assetToDelete) {
      const index = assets.indexOf(assetToDelete);
      if (assetToDelete.assetId < 0) {
        assets.splice(index, 1);
      } else {
        assets[index].entityState = 'Deleted';
        assets[index].requireEntityStateForChanges = true;
      }
      this.assetDeleted.emit(assetToDelete.assetId);
    }
    this.populateAssetItems(this._assets);
    this._cdr.markForCheck();
  }

  public onAssetEditClicked = (asset: AssetItem) => {
    const assetItemToEdit = this.assetItems.find(i => i.id === asset.id);
    if (!assetItemToEdit) {
      return;
    }
    const assetToEdit = this._assets?.find(e => e.assetId === assetItemToEdit.id);
    if (assetToEdit) {
      this.showAssetPopup(_.cloneDeep(assetToEdit));
    }
  }
}
